import React, { useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { isEqual, orderBy } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { Combobox } from '@headlessui/react'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import {
  useListenCouponsQuery,
  useListenProductCurrencyQuery,
  useListenStripeProductIdQuery,
  useListenStripeResponseQuery,
  useSetProductPriceMutation,
  useSetProductPricePromosMutation,
  useSetStripeRequestMutation,
} from 'modules/ProductInfo/productInfoApi'

import { Dialog, DialogContent } from 'common/components/Dialog/Dialog'
import { DeleteConfirmationBanner } from 'common/components/DeleteConfirmationBanner/DeleteConfirmationBanner'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import { createUID } from 'common/utils/createUID'
import PriceInput from './PriceInput'
import BillTypeInput from './BillTypeInput'
import { Switch } from 'common/components/Switch/Switch'
import { Input, inputBase } from 'common/components/Input/Input'
import CouponsSuggestions from '../CouponsSuggestions/CouponsSuggestions'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { usePopper } from 'common/hooks/usePopper'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'
import { MAX_TRIAL_DAYS } from '../../constants/trialConstants'
import { getIsAlwaysActive, getIsCouponActive, getProductCurrency } from 'modules/ProductInfo/utils/utils'
import { CURRENCY_DISPLAY } from 'common/constants/currencyConstants'
import PromoCoupon from '../PromoCoupon/PromoCoupon'

const Label = tw.label`inline-flex cursor-pointer font-semibold text-tBlack`
export const InputError = tw.p`flex text-xs mt-1 text-tRed`

export function PricingForm({ coachOrgId, plan, plans, closeForm, handleDelete, isCreateForm = false }) {
  const { createAlert } = useAlert()

  const [withTrial, setWithTrial] = useState(!!plan.trialPeriodDays)
  const [couponSearchValue, setCouponSearchValue] = useState('')
  const [trialPeriodErr, setTrialPeriodErr] = useState(null)
  const [billTypeErr, setBillTypeErr] = useState(null)
  const [priceErr, setPriceErr] = useState(null)
  const [isCouponEdited, setIsCouponEdited] = useState(false)

  const [setPricePromos] = useSetProductPricePromosMutation()
  const [setPrice] = useSetProductPriceMutation()
  const [setStripeRequest] = useSetStripeRequestMutation()

  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const { data: stripeResponse } = useListenStripeResponseQuery({ coachOrgId, responseId: plan?.id })
  const stripeResponsePending = stripeResponse?.status === 'pending'
  const { data: couponsData } = useListenCouponsQuery({ coachOrgId })
  const coupons = couponsData || {}
  const { data: currencyData } = useListenProductCurrencyQuery({ coachOrgId })
  const currency = getProductCurrency({ productCurrency: plan?.currency, defaultCurrency: currencyData })

  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [addCouponConfirmation, setAddCouponConfirmation] = useState(null)

  const planPromosToOrder = (plan?.promo || []).map((p) => ({
    ...p,
    isActive: getIsCouponActive({ promo: p, coupon: coupons?.[p?.stripeCouponId] }),
    expiration: getIsAlwaysActive({ promo: p, coupon: coupons?.[p?.stripeCouponId] })
      ? 0
      : coupons?.[p?.stripeCouponId]?.expiration,
  }))
  const orderedPlanPromos = orderBy(planPromosToOrder, ['isActive', 'expiration'], ['desc', 'desc']).map((p) => {
    delete p.isActive
    delete p.expiration
    return p
  })

  const defaultValues = {
    idx: plan.idx,
    id: plan.id,
    price: plan.price,
    time_unit: plan?.isLifetimePurchase ? 'lifetime' : plan.time_unit,
    time_val: plan?.isLifetimePurchase ? '1' : plan.time_val,
    promo: orderedPlanPromos,
    promoText: plan?.promoText || '',
    trialPeriodDays: plan?.trialPeriodDays || null,
    stripe_price_id: plan?.stripe_price_id || null,
    stripe_plan_id: plan?.stripe_price_id || plan?.stripe_plan_id || null,
    isLifetimePurchase: plan?.isLifetimePurchase || null,
    paymentType: plan?.paymentType || 'subscription',
  }
  const methods = useForm({
    defaultValues,
  })
  const {
    watch,
    setValue,
    register,
    formState: { errors },
    handleSubmit,
  } = methods

  const formState = watch()

  let usedBillingTypes = []
  plans.forEach((pln) => {
    if (pln.idx === plan.idx) {
      return
    }
    if (pln.time_unit === 'year' && pln.time_val === 1) {
      usedBillingTypes.push('year-1')
    } else if (pln.time_unit === 'month' && pln.time_val === 1) {
      usedBillingTypes.push('month-1')
    } else if (pln.time_unit === 'month' && pln.time_val === 6) {
      usedBillingTypes.push('month-6')
    } else {
      usedBillingTypes.push('lifetime-1')
    }
  })

  const validatePrice = (price) => {
    if (formState?.isLifetimePurchase && Number(price) < 29999) {
      setPriceErr(`Lifetime plan price should be at least ${CURRENCY_DISPLAY[currency]}299.99`)
      return false
    } else if (Number(price) < 499) {
      setPriceErr(`Price should be at least ${CURRENCY_DISPLAY[currency]}4.99`)
      return false
    } else {
      setPriceErr(null)
      return true
    }
  }

  const handleCreatePlan = async ({ formData }) => {
    const payload = {
      idx: formData.idx,
      stripeProductId,
      unitAmount: formData.price,
      interval: formData?.isLifetimePurchase ? 'lifetime' : formData.time_unit,
      intervalCount: formData?.isLifetimePurchase ? '1' : Number(formData.time_val),
      trybeProductId: formData.id,
      currency,
    }
    await setPrice({ coachOrgId, plan: formData, planId: formData.id })
    const requestId = createUID()
    // Awaited, and before the form closes, because this is the only thing that ever tells
    // the backend to create the price in Stripe. Left to run in the background it could
    // still be in flight when the coach navigates away, and then it never lands: the plan
    // exists in the database and looks finished, but it has no Stripe price, so nobody can
    // buy it and nothing on the page says why.
    await setStripeRequest({
      requestId,
      coachOrgId,
      type: 'CREATE_PRICE',
      payload,
    })
    closeForm()
    createAlert({ text: 'Plan created!', type: 'success' })
  }

  const handleUpdatePlan = async ({ defaultValues, formData }) => {
    const oneOfStripeFieldsChanged =
      formData.price !== defaultValues.price ||
      formData.time_unit !== defaultValues.time_unit ||
      Number(formData.time_val) !== Number(defaultValues.time_val)

    const updatedPlan = {
      ...formData,
      interval: formData?.isLifetimePurchase ? 'lifetime' : formData.time_unit,
      intervalCount: formData?.isLifetimePurchase ? '1' : Number(formData.time_val),
      currency: currency ? currency : null,
    }

    await setPrice({ coachOrgId, plan: updatedPlan, planId: formData.id })

    // Same reasoning as the create above: the request that re-prices the plan in Stripe has
    // to reach the database before the form closes, or a coach who moves on straight away
    // leaves the dashboard showing one price and Stripe charging another.
    if (oneOfStripeFieldsChanged) {
      const payload = {
        stripeProductId,
        unitAmount: formData.price,
        interval: formData?.isLifetimePurchase ? 'lifetime' : formData.time_unit,
        intervalCount: formData?.isLifetimePurchase ? '1' : Number(formData.time_val),
        stripePriceId: formData.stripe_price_id || formData.stripe_plan_id,
        trybeProductId: formData?.id,
        currency,
      }
      const requestId = createUID()
      await setStripeRequest({
        requestId,
        coachOrgId,
        type: 'UPDATE_PRICE',
        payload,
      })
    }

    createAlert({ text: 'Plan updated!', type: 'success' })
    closeForm()
  }

  const handleUpdateAutoApplied = ({ isAutoApplied, stripeCouponId }) => {
    setIsCouponEdited(true)
    const newPromos = orderedPlanPromos.map((p) =>
      p?.stripeCouponId === stripeCouponId
        ? { ...p, isAutoApplied }
        : {
            ...p,
            isAutoApplied: Boolean(p?.isAutoApplied) && isAutoApplied ? false : Boolean(p?.isAutoApplied),
          }
    )
    setPricePromos({ coachOrgId, planId: plan.id, promos: newPromos })
  }

  const onSubmit = async (data) => {
    const isPriceValid = validatePrice(data?.price)
    if (priceErr || !isPriceValid) {
      priceInputRef?.current?.focus()
      return
    } else if (trialPeriodErr) {
      trialPeriodInputRef?.current?.focus()
      return
    } else if (billTypeErr) {
      billTypeInputRef?.current?.focus()
      return
    }

    const trialDays = Number(data.trialPeriodDays) || null
    const formData = {
      ...data,
      promo: orderedPlanPromos,
      trialPeriodDays: data?.isLifetimePurchase ? null : trialDays,
      isLifetimePurchase: data?.isLifetimePurchase ? true : null,
      paymentType: data?.isLifetimePurchase ? 'one-time' : 'subscription',
    }

    if (isCreateForm) {
      handleCreatePlan({ formData })
    } else {
      handleUpdatePlan({ defaultValues, formData })
    }
  }

  const handleAddCoupon = async (couponId) => {
    const coupon = coupons[couponId]

    if (
      !addCouponConfirmation &&
      Boolean(coupon?.trialPeriodDays) &&
      Boolean(formState?.trialPeriodDays) &&
      Number(coupon.trialPeriodDays) < Number(formState.trialPeriodDays)
    ) {
      setAddCouponConfirmation(coupon)
      return
    }

    const pricingPromo = {
      stripeCouponId: coupon.id,
      isAutoApplied: false,
    }

    const newPromos = plan.promo ? [...plan.promo, pricingPromo] : [pricingPromo]
    await setPricePromos({ coachOrgId, planId: plan.id, promos: newPromos })
    createAlert({ text: 'Coupon is added', type: 'success' })
  }

  const handleDeletePromo = ({ stripeCouponId }) => {
    const newPromos = plan.promo.filter((pr) => pr.stripeCouponId !== stripeCouponId)
    setPricePromos({ coachOrgId, planId: plan.id, promos: newPromos })
    createAlert({ text: 'Coupon removed', type: 'success' })
  }

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  const { addInputRef, removeInputRef, moveFocusOnKeyPress } = useFormRefsControl()
  const inputRefsSortMethod = [
    'price',
    'billType',
    'promoText',
    'trialPeriodDays',
    'startDate',
    'endDate',
    'coupUpdateBtn',
    'couponSearch',
    'submit',
  ]
  const priceInputRef = useRef()
  const billTypeInputRef = useRef()
  const promoTextInputRef = useRef()
  const trialPeriodInputRef = useRef()
  const couponInputRef = useRef()
  const submitRef = useRef()
  useEffect(() => {
    addInputRef({ ref: priceInputRef, name: 'price', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: billTypeInputRef, name: 'billType', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: promoTextInputRef, name: 'promoText', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: couponInputRef, name: 'couponSearch', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: submitRef, name: 'submit', sortMethod: inputRefsSortMethod })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (withTrial) {
      addInputRef({ ref: trialPeriodInputRef, name: 'trialPeriodDays', sortMethod: inputRefsSortMethod })
    } else {
      removeInputRef(trialPeriodInputRef, 'trialPeriodDays')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withTrial])

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, () => onSubmit(formState)))

  return (
    <>
      <FormProvider {...methods}>
        <div className='divide-y divide-gray-200 overflow-auto' css={[!isCreateForm && tw`border-b border-gray-200`]}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            id='pricingForm'
            name='Pricing form'
            onKeyDown={(e) => {
              if (e.code === 'Enter') {
                e.preventDefault() //Otherwise form autosubmits on each enter press
              }
            }}
          >
            <div className='flex flex-col py-4 px-10'>
              <div className='mb-2'>
                <Label htmlFor='price' tw='mb-1'>
                  Price
                </Label>
                <PriceInput
                  inputRef={priceInputRef}
                  name='price'
                  placeholder='0.00'
                  value={formState.price}
                  onChange={(price) => {
                    validatePrice(price)
                    setValue('price', Number(price))
                  }}
                  error={Boolean(priceErr)}
                  autoFocus={true}
                  currency={currency}
                />
                {Boolean(priceErr) && <InputError>{priceErr}</InputError>}
              </div>
            </div>
            <div className='flex flex-col border-t border-gray-200 py-4 px-10'>
              <div className='mb-2'>
                <Label htmlFor='time_unit' tw='mb-1'>
                  Billing period
                </Label>
                <BillTypeInput
                  inputRef={billTypeInputRef}
                  value={`${formState.time_unit}-${formState.time_val}`}
                  setValue={setValue}
                  usedBillingTypes={usedBillingTypes}
                  error={billTypeErr}
                  setError={setBillTypeErr}
                />
                {errors && errors.time_unit && <InputError>{errors.time_unit.message}</InputError>}
              </div>
            </div>
            <div className='flex flex-col border-t border-gray-200 py-4 px-10'>
              <div className='mb-2'>
                <Label htmlFor='promoText' tw='mb-1'>
                  Promotional text
                </Label>
                <Input
                  inputRef={promoTextInputRef}
                  name='promoText'
                  register={register}
                  placeholder='Enter promotional text'
                />
                {errors && errors.promoText && <InputError>{errors.promoText.message}</InputError>}
              </div>
            </div>
            {formState?.isLifetimePurchase ? (
              <div className='flex flex-col border-t border-gray-200 py-4 px-10'>
                <p className='text-tBlack font-semibold'>Enable free trial</p>
                <p className='text-gray-500 text-sm'>Free trials are only compatible with subscriptions</p>
              </div>
            ) : (
              <div className='flex flex-col border-t border-gray-200 py-4 px-10'>
                <Switch
                  onChange={() => {
                    setWithTrial(!withTrial)
                    if (!withTrial) {
                      setValue('trialPeriodDays', 7)
                    } else {
                      setValue('trialPeriodDays', null)
                    }
                  }}
                  label='Enable free trial'
                  isChecked={withTrial}
                  labelClasses='flex-1 justify-between'
                  labelContainerClasses='!text-black'
                />
                {withTrial && (
                  <div className='mt-2'>
                    <Label htmlFor='trialPeriodDays' tw='mb-1 font-normal'>
                      Free trial length (in days)
                    </Label>
                    <Input
                      inputRef={trialPeriodInputRef}
                      type='number'
                      name='trialPeriodDays'
                      register={register}
                      min={1}
                      autoFocus={!defaultValues.trialPeriodDays}
                      registerOptions={{
                        onChange: (e) => {
                          const value = parseInt(e.target.value) || ''
                          if (value < 1) {
                            setTrialPeriodErr('Trial period must be at least 1 day')
                          } else if (Boolean(value) && value > MAX_TRIAL_DAYS) {
                            setTrialPeriodErr(`Trial period must be less than ${MAX_TRIAL_DAYS} days`)
                          } else {
                            setTrialPeriodErr(null)
                          }
                          setValue('trialPeriodDays', value)
                        },
                      }}
                      error={trialPeriodErr}
                    />
                    {trialPeriodErr && <InputError>{trialPeriodErr}</InputError>}
                  </div>
                )}
              </div>
            )}
            {!isCreateForm && (
              <div className='flex flex-col border-t border-gray-200 py-4 px-10'>
                <div className='flex items-center mb-1'>
                  <Label htmlFor='headlessui-combobox-input-1'>Eligible Coupons</Label>
                </div>
                <div className='relative mb-2'>
                  <Combobox
                    value={null}
                    onChange={(couponId) => {
                      if (couponId) {
                        handleAddCoupon(couponId)
                      }
                    }}
                  >
                    <div ref={popperTrigger} className='w-full'>
                      <Combobox.Input
                        name='couponSearch'
                        ref={(e) => {
                          couponInputRef.current = e
                        }}
                        placeholder={plan?.promo?.length > 10 ? '10 coupon limit reached' : 'Enable a coupon'}
                        disabled={plan?.promo?.length > 10}
                        autoComplete='off'
                        displayValue={(couponId) => (couponId ? coupons[couponId].name || '' : '')}
                        css={[inputBase, tw`px-4`]}
                        onChange={(e) => setCouponSearchValue(e.target.value)}
                      />
                    </div>
                    {couponSearchValue.length > 0 && (
                      <CouponsSuggestions
                        coupons={coupons}
                        searchValue={couponSearchValue}
                        product={plan}
                        inputRef={couponInputRef}
                        popperContainer={popperContainer}
                        currency={currency}
                        productType='plan'
                      />
                    )}
                  </Combobox>
                </div>
                {orderedPlanPromos.map((promo) => {
                  const coupon = Object.values(coupons).find((coupon) => coupon.id === promo.stripeCouponId)
                  return (
                    <PromoCoupon
                      promo={promo}
                      coupon={coupon}
                      handleDeletePromo={handleDeletePromo}
                      handleUpdateAutoApplied={handleUpdateAutoApplied}
                    />
                  )
                })}
              </div>
            )}
          </form>
        </div>
        {deleteConfirmation && !isCreateForm && (
          <DeleteConfirmationBanner
            text='Are you sure?'
            handleDelete={() => {
              closeForm()
              handleDelete()
            }}
            handleGoBack={() => setDeleteConfirmation(false)}
            loading={stripeResponsePending}
            containerClasses='shadow-none !px-4'
          />
        )}
        {!deleteConfirmation && (
          <UpdateActions
            ref={submitRef}
            itemKey={isCreateForm ? null : `${plan?.idx}`}
            containerClasses={isCreateForm ? null : 'shadow-none !px-4'}
            loading={stripeResponsePending}
            disabled={isCreateForm ? false : isEqual(formState, defaultValues) && !isCouponEdited}
            handleDelete={isCreateForm ? null : () => setDeleteConfirmation(true)}
            hideDelete={!`${plan?.idx}`}
            actionText={isCreateForm ? 'Add plan' : 'Save'}
            form='pricingForm'
          />
        )}
      </FormProvider>
      <Dialog open={Boolean(addCouponConfirmation)} setOpen={setAddCouponConfirmation}>
        <DialogContent>
          <CreateDialogBanner
            text={`If applied, the coupon that you are adding will reduce the plan trial period from ${plan?.trialPeriodDays} to ${addCouponConfirmation?.trialPeriodDays}.`}
            handleCreate={() => handleAddCoupon(addCouponConfirmation?.id)}
            createBtnText='Add'
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
